/* eslint-disable no-restricted-imports */
import { useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { formatDateToLocalStr } from '../../../../../utils/format/formatDate';
import { formatThousandsInt } from '../../../../../utils/format/formatThousands';
import { disableAdvertisingProfilePromoEnabled } from '../../../../../utils/useAdvertisingPromoEnabled';
import { useTranslation } from '../../../../../utils/useTranslation';
import { AdvertisingPromoTooltip } from '../../../../advertising/AdvertisingPromoTooltip/AdvertisingPromoTooltip';
import { IconForward } from '../../../../icon/IconForward';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Flex } from '../../../../layout/Flex';
import { Spin } from '../../../../layout/Spin/Spin';
import { Typography } from '../../../../typography/Typography';
import { useCpaRewardHandlers } from '../../hooks/useCpaRewardHandlers';
import { CpaButton } from '../../styles';
import { CpaRewardContentButtonProps } from '../../types';

export const CpaRewardContentButton = ({
    walletInfo,
    isWalletLoading,
    promo,
}: CpaRewardContentButtonProps) => {
    const t = useTranslation();

    const { balance, expireAt } = walletInfo || {};

    const { openInfoDialog } = useCpaRewardHandlers();

    const buttonContent = useMemo(() => {
        const text = balance
            ? `${t('Вывести')} ${formatThousandsInt(balance)} ₽`
            : t('Получать вознаграждение');

        return (
            <>
                <Flex spacing={0.125}>
                    <Typography
                        variant="caption"
                        weight="bold"
                        color="revert"
                        noWrap
                        style={{ letterSpacing: '0.7px' }}
                    >
                        {text}
                    </Typography>
                    {expireAt ? (
                        <Typography
                            weight="medium"
                            textTransform="none"
                            variant="caption2"
                            color="revert"
                            noWrap
                        >
                            {t(`до ${formatDateToLocalStr(expireAt)}`)}
                        </Typography>
                    ) : null}
                </Flex>
                {balance ? <IconForward width={18} height={18} /> : null}
            </>
        );
    }, [balance, expireAt, t]);

    const loader = useMemo(() => <Spin color="var(--color-invert)" size={20} />, []);

    const button = (
        <CpaButton
            onClick={openInfoDialog}
            isLoading={isWalletLoading}
            color={balance && promo ? 'accent' : undefined}
        >
            <Flex
                spacing={0.5}
                direction="row"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                {isWalletLoading ? loader : buttonContent}
            </Flex>
        </CpaButton>
    );

    useEffectOnce(() => {
        if (balance && promo) {
            disableAdvertisingProfilePromoEnabled();
        }
    });

    return (
        <Flex
            direction="row"
            alignItems="center"
            marginTop="8px"
            dataTestId={DataTestId.CPA_REWARD_CONTENT_BUTTON}
        >
            {balance && promo ? (
                <AdvertisingPromoTooltip
                    trigger={button}
                    options={{ placement: 'bottom' }}
                    text={t('Начните получать вознаграждение')}
                />
            ) : (
                button
            )}
        </Flex>
    );
};
